<script setup lang="ts">
import { ref, watch, computed, nextTick, onMounted } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { useStore } from 'vuex';

interface LabelObject {
  title: string;
  color: string;
}

interface Contact {
  id: number;
  name: string;
  email?: string;
  phone_number: string;
  labels: LabelObject[]; // Agora é array de objetos
  custom_attributes: Record<string, any>;
}

interface Label {
  id: number;
  title: string;
  color: string;
}

interface PreviewData {
  showing_count: number;
  contacts: Contact[];
  labels?: Label[];
}

interface CampaignData {
  title?: string;
  campaignName?: string;
  audience?: {
    labels: Array<{ id: number; type: string }>;
  };
}

interface Props {
  isOpen: boolean;
  campaignData?: CampaignData;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  close: [];
}>();

const store = useStore();

// Estados locais
const previewData = ref<PreviewData | null>(null);
const error = ref<string | null>(null);
const isLoading = ref(false);

// Computed para acessar o store
const audiencePreview = computed(() => store.getters['campaigns/getAudiencePreview']);
const isPreviewingAudience = computed(() => store.getters['campaigns/isPreviewingAudience']);

// Função para gerar cores de texto baseadas na cor de fundo
const getTextColor = (backgroundColor: string): string => {
  // Remove o # se existir
  const hex = backgroundColor.replace('#', '');
  
  // Converte para RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Calcula a luminância
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Retorna branco para cores escuras e preto para cores claras
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

// Função para gerar cor mais clara para o fundo
const getLightColor = (color: string, opacity: number = 0.2): string => {
  // Remove o # se existir
  const hex = color.replace('#', '');
  
  // Converte para RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// Função para carregar preview
const loadPreview = async () => {
  
  if (!props.campaignData?.audience?.labels?.length) {
    error.value = 'Nenhuma audiência configurada para esta campanha';
    return;
  }

  isLoading.value = true;
  error.value = null;

  try {
    await store.dispatch('campaigns/previewAudience', {
      audience: props.campaignData.audience,
      limit: 200
    });

    // Atualizar dados locais com a resposta do store
    if (audiencePreview.value) {
      previewData.value = audiencePreview.value;
    }

  } catch (err: any) {
    console.error('Erro ao carregar preview:', err);
    error.value = err.message || 'Erro ao carregar preview da audiência';
    previewData.value = null;
  } finally {
    isLoading.value = false;
  }
};

// Watch para sincronizar com o store
watch(audiencePreview, (newValue) => {
  if (newValue) {
    previewData.value = newValue;
    error.value = null;
  }
});

watch(isPreviewingAudience, (newValue) => {
  isLoading.value = newValue;
});

// Watch para carregar quando o modal abre - com immediate: true
watch(() => props.isOpen, (isOpen, oldValue) => {

  if (isOpen) {
    nextTick(() => {
      loadPreview();
    });
  } else {
    // Limpar dados quando fechar o modal
    previewData.value = null;
    error.value = null;
  }
}, { immediate: true });

// Garantir carregamento no mounted se o modal já estiver aberto
onMounted(() => {

  if (props.isOpen) {
    nextTick(() => {
      loadPreview();
    });
  }
});

// Função para fechar modal
const handleClose = () => {
  emit('close');
};
</script>

<template>
  <Teleport to="body">
    <Transition
      name="modal-fade"
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
        @click.self="handleClose"
      >
        <Transition
          name="modal-scale"
          enter-active-class="transition-all duration-300 ease-out"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition-all duration-200 ease-in"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-if="isOpen"
            class="bg-white dark:bg-woot-900 border border-woot-500 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            @click.stop
          >
            <!-- Header -->
            <div class="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
              <div class="flex items-center gap-3">
                <span class="i-lucide-users h-6 w-6 text-green-600"></span>
                <div>
                  <h2 class="text-xl font-semibold text-slate-900 dark:text-slate-100">
                    Lista de Transmissão
                  </h2>
                    <p class="text-sm text-slate-600 dark:text-slate-400">
                    {{ campaignData?.campaignName?.split(' - ')[1] }}
                    </p>
                </div>
              </div>

              <button
                @click="handleClose"
                class="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              >
                <span class="i-lucide-x h-5 w-5 text-slate-500"></span>
              </button>
            </div>

            <!-- Content -->
            <div class="flex flex-col h-full max-h-[calc(90vh-80px)]">
              
              <!-- Loading State -->
              <div v-if="isLoading" class="flex-1 flex items-center justify-center p-8">
                <div class="text-center">
                  <div class="animate-spin h-8 w-8 border-4 border-green-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p class="text-slate-600 dark:text-slate-400">Carregando preview da audiência...</p>
                </div>
              </div>

              <!-- Error State -->
              <div v-else-if="error" class="flex-1 flex items-center justify-center p-8">
                <div class="text-center max-w-md">
                  <span class="i-lucide-alert-circle h-12 w-12 text-red-500 mx-auto mb-4"></span>
                  <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                    Erro ao carregar preview
                  </h3>
                  <p class="text-slate-600 dark:text-slate-400 mb-4">{{ error }}</p>
                  <button
                    @click="loadPreview"
                    class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Tentar Novamente
                  </button>
                </div>
              </div>

              <!-- Preview Data -->
              <div v-else-if="previewData" class="flex-1 flex flex-col overflow-hidden">
                
                <!-- Stats Header -->
                <div class="p-6 border-b border-slate-200 dark:border-slate-700">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-6">
                      <div class="text-center">
                        <div class="text-2xl font-bold text-green-600">
                          {{ previewData.showing_count }}
                        </div>
                        <div class="text-sm text-slate-600 dark:text-slate-400">
                          Contatos Encontrados
                        </div>
                      </div>
                    </div>
                    
                    <button
                      @click="loadPreview"
                      class="flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-800 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 transition-colors"
                    >
                      <span class="i-lucide-refresh-cw h-4 w-4"></span>
                      Atualizar
                    </button>
                  </div>
                </div>

                <!-- Contacts List -->
                <div class="flex-1 overflow-auto p-6">
                  <div v-if="previewData.contacts?.length > 0" class="space-y-3">
                    <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100 mb-4">
                      Contatos ({{ previewData.contacts.length }})
                    </h3>
                    
                    <div class="grid gap-3">
                      <div
                        v-for="contact in previewData.contacts"
                        :key="contact.id"
                        class="p-4 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                      >
                        <div class="flex items-start justify-between">
                          <!-- Contact Info -->
                          <div class="flex-1">
                            <div class="flex items-center gap-3 mb-2">
                              <span class="i-lucide-user h-4 w-4 text-slate-500"></span>
                              <h4 class="font-medium text-slate-900 dark:text-slate-100">
                                {{ contact.name || 'Sem nome' }}
                              </h4>
                            </div>
                            
                            <div class="space-y-1">
                              <div v-if="contact.phone_number" class="flex items-center gap-2 text-sm">
                                <span class="i-lucide-phone h-3 w-3 text-slate-500"></span>
                                <span class="font-mono text-slate-600 dark:text-slate-400">
                                  {{ contact.phone_number }}
                                </span>
                              </div>
                              
                              <div v-if="contact.email" class="flex items-center gap-2 text-sm">
                                <span class="i-lucide-mail h-3 w-3 text-slate-500"></span>
                                <span class="text-slate-600 dark:text-slate-400">
                                  {{ contact.email }}
                                </span>
                              </div>
                            </div>
                          </div>

                          <!-- Labels com cores vindas do backend -->
                          <div v-if="contact.labels?.length > 0" class="flex flex-wrap gap-1 ml-4">
                            <span
                              v-for="label in contact.labels"
                              :key="label.title"
                              class="px-2 py-1 text-xs font-medium rounded-full"
                              :style="{
                                backgroundColor: getLightColor(label.color, 0.2),
                                color: label.color,
                                border: `1px solid ${label.color}`
                              }"
                            >
                              {{ label.title }}
                            </span>
                          </div>
                        </div>

                        <!-- Custom Attributes -->
                        <div v-if="contact.custom_attributes && Object.keys(contact.custom_attributes).length > 0" class="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                          <div class="text-xs text-slate-500 mb-2">Atributos Personalizados:</div>
                          <div class="flex flex-wrap gap-2">
                            <span
                              v-for="[key, value] in Object.entries(contact.custom_attributes)"
                              :key="key"
                              class="px-2 py-1 text-xs bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded"
                            >
                              {{ key }}: {{ value }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Empty State -->
                  <div v-else class="text-center py-12">
                    <span class="i-lucide-users-x h-12 w-12 text-slate-400 mx-auto mb-4"></span>
                    <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                      Nenhum contato encontrado
                    </h3>
                    <p class="text-slate-600 dark:text-slate-400">
                      Não foram encontrados contatos para os critérios da audiência.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Empty State Initial -->
              <div v-else class="flex-1 flex items-center justify-center p-8">
                <div class="text-center">
                  <span class="i-lucide-search h-12 w-12 text-slate-400 mx-auto mb-4"></span>
                  <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                    Lista de Transmissão
                  </h3>
                  <p class="text-slate-600 dark:text-slate-400 mb-4">
                    Clique no botão abaixo para visualizar os contatos desta campanha.
                  </p>
                  <button
                    @click="loadPreview"
                    class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Carregar Preview
                  </button>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="flex items-center justify-end gap-3 p-6 border-t border-slate-200 dark:border-slate-700">
            <button
                @click="handleClose"
                class="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
                Fechar
            </button>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Animações do modal */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-scale-enter-active,
.modal-scale-leave-active {
  transition: all 0.3s ease;
}

.modal-scale-enter-from,
.modal-scale-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* Scrollbar customizada */
.overflow-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.dark .overflow-auto::-webkit-scrollbar-thumb {
  background: #475569;
}

/* Estados de foco */
button:focus-visible {
  @apply ring-2 ring-green-500 ring-offset-2 ring-offset-white dark:ring-offset-slate-900;
}

/* Spinner animation */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Responsive */
@media (max-width: 768px) {
  .max-w-4xl {
    max-width: 95vw;
  }
  
  .p-6 {
    padding: 1rem;
  }
}
</style>